import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Capture, CaptureRecord } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { loadedAt, orAbsent, shortDate } from "../lib/format";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";

/* The records one page sent from the browser button produced.
 *
 * The extension's popup links its record count here, which James asked for on
 * 2026-09-30 so a person can click through from what was found to the records
 * themselves. Engine migration 128: api.capture for the heading, and
 * api.capture_record for the records, one row each, merged records shown as
 * the record they were merged into. */

const KIND_ORDER = ["company", "person", "property", "project"] as const;
const KIND_LABEL: Record<string, string> = {
  company: "Company", person: "Person", property: "Property", project: "Project",
};
const LIST: Record<string, "companies" | "people" | "properties"> = {
  company: "companies", person: "people", property: "properties",
};
/* A page still being read keeps checking, the way the popup does. */
/* The processing_status values before resolved, needs_review, ignored or
   failed, from chk_signal_processing. */
const IN_FLIGHT = new Set(["new", "triaged", "parsed"]);

export function CaptureRecords({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const page = useQuery({
    queryKey: ["capture", id],
    queryFn: async () => {
      const { data, error } = await apiClient(session.idToken)
        .from("capture")
        .select("id,url,title,submitted_by,submitted_at,processing_status")
        .eq("id", id)
        .limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as Capture[])[0] ?? null;
    },
    refetchInterval: (q) => (IN_FLIGHT.has(q.state.data?.processing_status ?? "") ? 10000 : false),
  });

  const records = useQuery({
    queryKey: ["capture-records", id],
    queryFn: async () => {
      const { data, error } = await apiClient(session.idToken)
        .from("capture_record")
        .select("signal_id,entity_type,entity_id,label,address,is_new")
        .eq("signal_id", id)
        .limit(1000);
      if (error) throw new Error(apiErrorMessage(error));
      const rows = (data ?? []) as CaptureRecord[];
      return rows.sort((a, b) =>
        KIND_ORDER.indexOf(a.entity_type as typeof KIND_ORDER[number])
          - KIND_ORDER.indexOf(b.entity_type as typeof KIND_ORDER[number])
        || Number(Boolean(b.is_new)) - Number(Boolean(a.is_new))
        || (a.label ?? a.address ?? "").localeCompare(b.label ?? b.address ?? ""));
    },
    refetchInterval: IN_FLIGHT.has(page.data?.processing_status ?? "") ? 10000 : false,
  });

  const rows = records.data ?? [];
  const fresh = rows.filter((r) => r.is_new).length;
  const busy = page.isFetching || records.isFetching;

  return (
    <main>
      <h1>Records from a page</h1>
      {page.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : page.isError ? <p className="refusal">{(page.error as Error).message}</p>
        : !page.data ? <p className="refusal">No page sent from the browser button has this id.</p>
        : (
          <p>
            {page.data.url
              ? <a href={page.data.url} target="_blank" rel="noreferrer">{page.data.title || page.data.url}</a>
              : orAbsent(page.data.title)}
            <span className="muted"> · sent by {orAbsent(page.data.submitted_by)}, {shortDate(page.data.submitted_at)}</span>
          </p>
        )}

      <div className="bar">
        <span className="muted">
          {records.isSuccess && rows.length
            ? `${rows.length} records: ${fresh} new, ${rows.length - fresh} already in EVIE`
            : ""}
        </span>
        <span style={{ marginLeft: "auto" }} className="muted">{loadedAt(records.dataUpdatedAt)}</span>
        <button
          disabled={busy}
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ["capture", id] });
            queryClient.invalidateQueries({ queryKey: ["capture-records", id] });
          }}
        >
          {busy ? "Refreshing" : "Refresh"}
        </button>
      </div>

      {records.isLoading ? <p className="muted"><Spinner />Loading the records.</p>
        : records.isError ? <p className="refusal">{(records.error as Error).message}</p>
        : !rows.length ? (
          IN_FLIGHT.has(page.data?.processing_status ?? "")
            ? <p className="muted"><Spinner />This page is still being read.</p>
            : <p className="muted">This page produced no records.</p>
        ) : (
          <table>
            <thead><tr><th>Type</th><th>Record</th><th>Address</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.entity_type}-${r.entity_id}`}>
                  <td>{KIND_LABEL[r.entity_type ?? ""] ?? r.entity_type}</td>
                  <td>
                    {/* A project has no record screen in this release, so it
                        shows as its name. */}
                    {(() => {
                      const list = LIST[r.entity_type ?? ""];
                      return list
                        ? <RecordLink to={list} id={r.entity_id} label={r.label ?? r.address} />
                        : orAbsent(r.label);
                    })()}
                  </td>
                  <td className="muted">{r.entity_type === "property" ? orAbsent(r.address) : ""}</td>
                  <td className="muted">{r.is_new ? "New" : "Already in EVIE"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </main>
  );
}
